/**
 *   Created by Arif Topcu in 2019
 */
import React, {useMemo} from 'react';
import {startRequests} from 'state/app/actions';
import {useOnChangeAccount} from 'state/selections';
import {commitSelections} from 'state/selections/actions';
import styles from './MainTitle.module.css';
import {getEntityName, EntityType} from '@ls/entity-service';
import {
  selectSelectedAccountEntity,
  selectSelectedAccountEntityId,
  selectSelectedBenchmarkEntity, selectSelectedBenchmarkEntityId,
} from 'state/selections/selectors';
import SelectionState from 'state/selections/SelectionState';
import {useDispatch, useSelector} from 'react-redux';
import {isDefined} from '@ls/js-utils';
import {selectEntities, selectFetchingEntities} from 'state/data/selectors';
import {ClipLoader} from 'react-spinners';
import {selectIsDrawerOpen} from 'state/app/selectors';
import styled from 'styled-components';
import Tooltip from '@ls/react-tooltip';

const AccLabel = styled.span`
  margin: 0 5px;
`;
const MajorMinorBtn = styled.span`
  ${({hasMajorEntity}) => {
  if (hasMajorEntity) {
    return `
        text-decoration: underline;
        cursor: pointer;
        &:hover {
          font-size: 110%;
        }
      `
  }
}}
`;
const MainTitle = (props) => {
  const entities = useSelector(selectEntities);
  const selectedAccountEntityId = useSelector(selectSelectedAccountEntityId(SelectionState.COMMITTED));
  const selectedBenchmarkEntityId = useSelector(selectSelectedBenchmarkEntityId(SelectionState.COMMITTED));
  const selectedAccountEntity = useSelector(selectSelectedAccountEntity(SelectionState.COMMITTED));
  const selectedBenchmarkEntity = useSelector(selectSelectedBenchmarkEntity(SelectionState.COMMITTED));
  const fetchingEntities = useSelector(selectFetchingEntities);
  const isDrawerOpen = useSelector(selectIsDrawerOpen);
  const dispatch = useDispatch();
  const accLabel = useMemo(() => {
    if (!isDefined(selectedAccountEntityId)) {
      return null;
    }
    return getEntityName(selectedAccountEntity, {suppressMajorMinor: true}) ?? selectedAccountEntityId;
  }, [selectedAccountEntity, selectedAccountEntityId]);

  const accMajorMinorLabel = useMemo(() => {
    if (selectedAccountEntity?.type === EntityType.BMARK) {
      return null;
    }
    if (selectedAccountEntity) {
      return (selectedAccountEntity.minor ? `${selectedAccountEntity.major || ''}/${selectedAccountEntity.minor}` : `${selectedAccountEntity.major}/`);
    }
  }, [selectedAccountEntity]);

  const bmkLabel = useMemo(() => {
    if (!isDefined(selectedBenchmarkEntityId)) {
      return null;
    }
    return getEntityName(selectedBenchmarkEntity) ?? selectedBenchmarkEntityId;
  }, [selectedBenchmarkEntity, selectedBenchmarkEntityId]);

  const onChangeAccount = useOnChangeAccount();

  const majorEntity = useMemo(() => {
    if (selectedAccountEntity?.major?.length && selectedAccountEntity?.minor?.length) { //this entity has both major and minor, which means we can link to major
      return entities?.find(entity => entity.major === selectedAccountEntity.major && entity.minor === null);
    }
  }, [entities, selectedAccountEntity]);

  const onClickMajor = useMemo(() => {
    if (majorEntity) { //this entity has both major and minor, which means we can link to major
      return () => {
        onChangeAccount(majorEntity.entityId);
        dispatch(commitSelections());
        dispatch(startRequests());
      }
    }
  }, [dispatch, majorEntity, onChangeAccount]);

  return (
    <div className={styles.root}>
      {!isDrawerOpen && fetchingEntities && (
        <>
          <div className={styles.spinner}>
            <ClipLoader
              size={28}
              color={'#ffffff'}
            />
          </div>
          <div className={styles.mask}/>
        </>
      )}
      <div className={styles.acc}>
        <AccLabel>{accLabel}</AccLabel>
        {accMajorMinorLabel && (
          <>
            (
            {majorEntity
              ? (
                <>
                  <Tooltip label={`Load Major (${getEntityName(majorEntity)})`}>
                    <MajorMinorBtn onClick={onClickMajor} hasMajorEntity={!!majorEntity}>
                      {selectedAccountEntity.major}
                    </MajorMinorBtn>
                  </Tooltip>
                  /{selectedAccountEntity.minor}
                </>
              ) : accMajorMinorLabel
            }
            )
          </>
        )}
      </div>
      {bmkLabel && <div className={styles.bmk}><span className={styles.vs}>vs</span>{bmkLabel}</div>}
    </div>
  );
};

export default MainTitle;
